import { FC, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrency, formatNumber } from 'utils/formats';
import { HoldingClass, Partner } from 'types';
import { TradingOffer } from 'types/TradingOffer';
import { TradingOfferOperation } from 'types/TradingOfferOperation';

import TradingOfferStatusBadge from './TradingOfferStatusBadge';
import getOfferReference from '../utils/getOfferReference';
import EquityMarketAdminPanelOperationsTable from './EquityMarketAdminPanelOperationsTable';

/* eslint-disable no-unused-vars -- callback param names in type definitions */
type EquityMarketAdminPanelOffersListProps = {
  sellOffers: TradingOffer[];
  buyOffers: TradingOffer[];
  operations: TradingOfferOperation[];
  onViewOfferDetail: (offerId: string) => void;
  onViewOperationDetail: (operationId: string) => void;
  onUpdateOperationStatus: (
    operationId: string,
    status: string,
    comment?: string
  ) => void;
};
/* eslint-enable no-unused-vars */

type TabKey = 'SELL' | 'BUY' | 'OPERATIONS';

const getOfferStatus = (offer: TradingOffer) => {
  const now = new Date();
  const endDate = new Date(offer.endDate);

  if (endDate < now) return 'EXPIRED';
  if (offer.sharesCount.agreed === offer.sharesCount.total) return 'ACCEPTED';
  return 'ACTIVE';
};

const getPartnerName = (partner: string | Partial<Partner>) => {
  if (typeof partner === 'string') return '';
  return partner?.name || '';
};

const getShareClassName = (
  shareClass: string | Partial<HoldingClass> | null
) => {
  if (!shareClass || typeof shareClass === 'string') return '-';
  return shareClass?.name || '-';
};

const EquityMarketAdminPanelOffersList: FC<
  EquityMarketAdminPanelOffersListProps
> = ({
  sellOffers,
  buyOffers,
  operations,
  onViewOfferDetail,
  onViewOperationDetail,
  onUpdateOperationStatus,
}) => {
  const { t } = useTranslate();
  const [activeTab, setActiveTab] = useState<TabKey>('SELL');

  const offers = activeTab === 'SELL' ? sellOffers : buyOffers;

  return (
    <div className="card card-bordered">
      <div className="card-inner">
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link px-2 ${
                activeTab === 'SELL' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('SELL')}
            >
              <em className="icon ni ni-wallet-out mr-1" />
              {t('SellOffers')}
              <span className="badge badge-pill badge-dim badge-success ml-1">
                {sellOffers.length}
              </span>
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link px-2 ${activeTab === 'BUY' ? 'active' : ''}`}
              onClick={() => setActiveTab('BUY')}
            >
              <em className="icon ni ni-wallet-in mr-1" />
              {t('BuyOffers')}
              <span className="badge badge-pill badge-dim badge-primary ml-1">
                {buyOffers.length}
              </span>
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link px-2 ${
                activeTab === 'OPERATIONS' ? 'active' : ''
              }`}
              onClick={() => setActiveTab('OPERATIONS')}
            >
              <em className="icon ni ni-list-check mr-1" />
              {t('Operations')}
              <span className="badge badge-pill badge-dim badge-warning ml-1">
                {operations.length}
              </span>
            </button>
          </li>
        </ul>

        {activeTab === 'OPERATIONS' && (
          <EquityMarketAdminPanelOperationsTable
            operations={operations}
            offers={[...sellOffers, ...buyOffers]}
            onViewOperationDetail={onViewOperationDetail}
            onUpdateOperationStatus={onUpdateOperationStatus}
            embedded
          />
        )}
        {activeTab !== 'OPERATIONS' && offers.length === 0 && (
          <div className="text-center py-4">
            <em className="icon ni ni-inbox fs-3 text-soft d-block mb-2" />
            <p className="text-soft">{t('NoOffersAvailable')}</p>
          </div>
        )}
        {activeTab !== 'OPERATIONS' && offers.length > 0 && (
          <div className="nk-iv-scheme-list">
            {offers.map((offer) => {
              const status = getOfferStatus(offer);
              const partnerName = getPartnerName(offer.partner);
              const shareClassName = getShareClassName(offer.shareClass);
              const totalAmount = offer.sharesCount.total * offer.sharePrice;
              const isSell = activeTab === 'SELL';

              return (
                <div
                  key={offer._id}
                  className={`nk-iv-scheme-item cursor-pointer hover-shadow ${
                    isSell
                      ? 'border-left-2 border-left-success'
                      : 'border-left-2 border-left-primary'
                  }`}
                  onClick={() => onViewOfferDetail(offer._id)}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) =>
                    e.key === 'Enter' && onViewOfferDetail(offer._id)
                  }
                >
                  <div
                    className={`nk-iv-scheme-icon ${
                      status === 'ACTIVE' ? 'is-running' : ''
                    }`}
                  >
                    <em
                      className={`icon ni ${
                        isSell ? 'ni-wallet-out' : 'ni-wallet-in'
                      }`}
                    />
                  </div>

                  <div className="nk-iv-scheme-info nk-iv-scheme-info-stack text-start">
                    <div className="nk-iv-scheme-block-left">
                      <div className="nk-iv-scheme-name">
                        {formatNumber(offer.sharesCount.total)}{' '}
                        {t('Shares').toLowerCase()}
                      </div>
                      <div className="nk-iv-scheme-desc text-soft">
                        {t('Class')}:&nbsp;
                        <span className="text-dark">{shareClassName}</span>
                      </div>
                    </div>

                    <div className="nk-iv-scheme-block-right">
                      <div className="nk-iv-scheme-desc">
                        {t('PricePerShare')}:&nbsp;
                        <span className="amount">
                          {formatCurrency(offer.sharePrice)}
                        </span>
                      </div>
                      <div className="nk-iv-scheme-desc text-soft">
                        {t('Total')}:&nbsp;
                        <span className="amount">
                          {formatCurrency(totalAmount)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="nk-iv-scheme-term">
                    <div className="nk-iv-scheme-start nk-iv-scheme-order">
                      <span className="nk-iv-scheme-label text-soft">
                        {isSell ? t('Seller') : t('Buyer')}
                      </span>
                      <span className="nk-iv-scheme-value text-dark">
                        {partnerName}
                      </span>
                    </div>
                  </div>

                  <div className="nk-iv-scheme-amount">
                    <div className="nk-iv-scheme-amount-a nk-iv-scheme-order">
                      <span className="nk-iv-scheme-label text-soft">
                        {t('Ref')}
                      </span>
                      <span className="nk-iv-scheme-value text-dark fs-12px">
                        {getOfferReference(offer)}
                      </span>
                    </div>
                    <div className="nk-iv-scheme-amount-b nk-iv-scheme-order">
                      <span className="nk-iv-scheme-label text-soft">
                        {t('Status')}
                      </span>
                      <TradingOfferStatusBadge status={status} />
                    </div>
                  </div>

                  <div className="nk-iv-scheme-more">
                    <button
                      type="button"
                      className="btn btn-icon btn-lg btn-round btn-trans"
                      title={t('ViewDetail')}
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewOfferDetail(offer._id);
                      }}
                    >
                      <em className="icon ni ni-forward-ios" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EquityMarketAdminPanelOffersList;
